// PlayFirst Clinical — Therapist's Parent Links manager.
// Invite parents by email, see linked families, push activities, view their
// Language Maps (read-only).
//
// /clinical/parent-links
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Heart, UserPlus, Send, Eye, Loader2, Trash2, Clock, CheckCircle2, XCircle, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_PILL = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-800', icon: Clock },
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  declined: { label: 'Declined', cls: 'bg-stone-100 text-stone-600', icon: XCircle },
  revoked: { label: 'Revoked', cls: 'bg-rose-100 text-rose-800', icon: XCircle },
};

export default function ClinicalParentLinks() {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteClinicalChildId, setInviteClinicalChildId] = useState('');
  const [clinicalChildren, setClinicalChildren] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [pushLink, setPushLink] = useState(null);   // {link} when push dialog open
  const [mapLink, setMapLink] = useState(null);     // {link} when map dialog open
  const [mapData, setMapData] = useState(null);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const [linksResp, childrenResp] = await Promise.all([
        axios.get(`${API}/therapist/parent-links`, auth()),
        // Pull the therapist's caseload so they can pick which clinical
        // child this parent maps to. Feeding `clinical_child_id` into
        // the invite is what wires up the "With my therapist" tab in
        // the unified /parent-home shell — without it the parent sees
        // the tab but no children in it.
        axios.get(`${API}/children`, auth()).catch(() => ({ data: [] })),
      ]);
      setLinks(linksResp.data?.links || []);
      setClinicalChildren(childrenResp.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Therapist access only');
        navigate('/');
      } else {
        toast.error("Couldn't load links");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    try {
      const body = { parent_email: email };
      if (inviteClinicalChildId) body.clinical_child_id = inviteClinicalChildId;
      await axios.post(`${API}/therapist/parent-links`, body, auth());
      toast.success(`Invite sent to ${email}`);
      setInviteEmail('');
      setInviteClinicalChildId('');
      await fetchLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send invite');
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (link) => {
    if (!window.confirm(`Revoke link with ${link.parent_email}? They'll keep any ideas you've already pushed but you won't be able to push new ones.`)) return;
    try {
      await axios.delete(`${API}/therapist/parent-links/${link.id}`, auth());
      toast.success('Link revoked');
      await fetchLinks();
    } catch {
      toast.error('Could not revoke');
    }
  };

  const approveParentInitiated = async (link) => {
    if (!window.confirm(`Approve ${link.parent_email}'s request to connect? They'll gain access to push activities and the Language Map.`)) return;
    try {
      await axios.post(`${API}/therapist/parent-links/${link.id}/approve`, {}, auth());
      toast.success(`${link.parent_email} approved`);
      await fetchLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    }
  };

  const openMap = async (link) => {
    setMapLink(link);
    setMapData(null);
    try {
      const r = await axios.get(`${API}/therapist/parent-links/${link.id}/map`, auth());
      setMapData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Language Map');
      setMapLink(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="max-w-5xl mx-auto px-4 sm:px-8 py-5 flex items-center">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-stone-600 hover:text-stone-900" data-testid="parent-links-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-8 pb-12">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-700 flex items-center justify-center">
            <Heart className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-serif text-stone-900">Parent links</h1>
            <p className="text-sm text-stone-500">Invite the parents you work with to share their child&apos;s Language Map and push play ideas straight into their feed.</p>
          </div>
        </div>

        {/* Invite */}
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5" data-testid="invite-parent-panel">
          <h2 className="font-medium text-stone-900 mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#7C6F9C]" /> Invite a parent
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1">
              <Label className="text-xs">Parent&apos;s PFP email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="parent@example.com"
                className="h-10 rounded-xl"
                data-testid="invite-parent-email"
              />
            </div>
            {clinicalChildren.length > 0 && (
              <div className="flex-1">
                <Label className="text-xs">Link to which of your clients?</Label>
                <select
                  value={inviteClinicalChildId}
                  onChange={(e) => setInviteClinicalChildId(e.target.value)}
                  className="w-full h-10 rounded-xl border border-stone-200 bg-white px-3 text-sm focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
                  data-testid="invite-clinical-child-select"
                >
                  <option value="">Just share the Language Map (no client link)</option>
                  {clinicalChildren.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
            <Button
              onClick={invite}
              disabled={inviting || !inviteEmail.trim()}
              className="h-10 bg-[#7C6F9C] hover:bg-[#6a5d8b] text-white rounded-xl px-5"
              data-testid="invite-parent-submit"
            >
              {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Send invite
            </Button>
          </div>
          <p className="text-xs text-stone-500 mt-2 leading-relaxed">
            They&apos;ll see your request the next time they open PlayFirst Parent. Once they accept,
            you&apos;ll be able to view their child&apos;s Language Map and push tailored play ideas.
            {inviteClinicalChildId && (
              <> The parent will also see this child&apos;s clinical profile under <em>&quot;With my therapist&quot;</em> in their PlayFirst home.</>
            )}
          </p>
        </div>

        {/* Linked families */}
        <div className="mt-6">
          <h2 className="font-medium text-stone-900 mb-3">Your linked families</h2>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-stone-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : links.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-200 bg-white p-8 text-center text-stone-500" data-testid="parent-links-empty">
              No linked parents yet. Send your first invite above.
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((l) => {
                const pill = STATUS_PILL[l.status] || STATUS_PILL.pending;
                const PillIcon = pill.icon;
                return (
                  <div
                    key={l.id}
                    className="rounded-2xl border border-stone-200 bg-white p-4 sm:flex sm:items-center sm:justify-between"
                    data-testid={`parent-link-row-${l.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-stone-900 truncate">{l.parent_name || l.parent_email}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${pill.cls}`}>
                          <PillIcon className="w-3 h-3" /> {pill.label}
                        </span>
                        {l.initiated_by === 'parent' && l.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            Parent requested
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 mt-1">
                        {l.child_name ? `Child: ${l.child_name}` : 'Awaiting child selection'}
                        {l.last_pushed_at && ` · Last push ${new Date(l.last_pushed_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-3 sm:mt-0">
                      {l.initiated_by === 'parent' && l.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => approveParentInitiated(l)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`parent-link-approve-${l.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {l.status === 'active' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openMap(l)} data-testid={`parent-link-map-${l.id}`}>
                            <Eye className="w-4 h-4 mr-1" /> Map
                          </Button>
                          <Button size="sm" onClick={() => setPushLink(l)} className="bg-[#7C6F9C] hover:bg-[#6a5d8b] text-white" data-testid={`parent-link-push-${l.id}`}>
                            <Send className="w-4 h-4 mr-1" /> Push idea
                          </Button>
                        </>
                      )}
                      {(l.status === 'active' || l.status === 'pending') && (
                        <Button size="sm" variant="ghost" onClick={() => revoke(l)} className="text-rose-600 hover:bg-rose-50" data-testid={`parent-link-revoke-${l.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Push idea dialog */}
      {pushLink && (
        <PushIdeaDialog
          link={pushLink}
          onClose={() => setPushLink(null)}
          onPushed={() => { setPushLink(null); fetchLinks(); }}
        />
      )}

      {/* Language map dialog */}
      {mapLink && (
        <MapDialog
          link={mapLink}
          data={mapData}
          onClose={() => { setMapLink(null); setMapData(null); }}
        />
      )}
    </div>
  );
}

function PushIdeaDialog({ link, onClose, onPushed }) {
  const [form, setForm] = useState({
    activity_name: '',
    what_to_do: '',
    phrases: '',
    therapist_note: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.activity_name.trim() || !form.what_to_do.trim()) {
      toast.error('Activity name and "what to do" are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        activity_name: form.activity_name.trim(),
        what_to_do: form.what_to_do.trim(),
        phrases_to_model: form.phrases.split('\n').map((s) => s.trim()).filter(Boolean),
        therapist_note: form.therapist_note.trim(),
      };
      await axios.post(`${API}/therapist/parent-links/${link.id}/push`, body, auth());
      toast.success(`Pushed to ${link.parent_email}`);
      onPushed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not push');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" data-testid="push-idea-dialog">
        <h3 className="font-serif text-lg text-stone-900 mb-1">Push an idea to {link.parent_name || link.parent_email}</h3>
        <p className="text-xs text-stone-500 mb-3">Lands in their feed as today&apos;s play idea, with your name on it.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Activity name</Label>
            <Input value={form.activity_name} onChange={(e) => setForm((d) => ({ ...d, activity_name: e.target.value }))} maxLength={140} data-testid="push-idea-name" />
          </div>
          <div>
            <Label className="text-xs">What to do</Label>
            <Textarea rows={4} value={form.what_to_do} onChange={(e) => setForm((d) => ({ ...d, what_to_do: e.target.value }))} maxLength={2000} data-testid="push-idea-what" />
          </div>
          <div>
            <Label className="text-xs">Phrases to model (one per line)</Label>
            <Textarea rows={3} value={form.phrases} onChange={(e) => setForm((d) => ({ ...d, phrases: e.target.value }))} placeholder={"e.g.\nMore bubbles!\nPop pop pop!"} data-testid="push-idea-phrases" />
          </div>
          <div>
            <Label className="text-xs">A note for the parent (optional)</Label>
            <Textarea rows={2} value={form.therapist_note} onChange={(e) => setForm((d) => ({ ...d, therapist_note: e.target.value }))} placeholder="Why this idea, what we tried in session, etc." data-testid="push-idea-note" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} data-testid="push-idea-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#7C6F9C] hover:bg-[#6a5d8b] text-white" data-testid="push-idea-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Push idea
          </Button>
        </div>
      </div>
    </div>
  );
}

function MapDialog({ link, data, onClose }) {
  const m = data?.language_map?.map || null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" data-testid="parent-link-map-dialog">
        <h3 className="font-serif text-lg text-stone-900 mb-1">{link.child_name || 'Child'}&apos;s Language Map</h3>
        <p className="text-xs text-stone-500 mb-3">Read-only · last refreshed {data?.language_map?.generated_at?.slice(0, 10) || '—'}</p>
        {!data && (
          <div className="py-8 text-center text-stone-400">
            <Loader2 className="w-5 h-5 animate-spin inline" />
          </div>
        )}
        {data && !m && (
          <p className="text-sm text-stone-500">No Language Map yet — the parent hasn&apos;t completed onboarding.</p>
        )}
        {m && (
          <div className="space-y-3 text-sm text-stone-700">
            {m.current_communication_summary && (
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">How they communicate</p>
                <p>{m.current_communication_summary}</p>
              </div>
            )}
            {Array.isArray(m.strengths) && m.strengths.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Strengths</p>
                <ul className="list-disc pl-5 space-y-0.5">{m.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {Array.isArray(m.helpful_supports) && m.helpful_supports.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">What helps</p>
                <ul className="list-disc pl-5 space-y-0.5">{m.helpful_supports.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {m.emerging_focus && (
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Emerging focus</p>
                <p>{m.emerging_focus}</p>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end mt-5">
          <Button variant="outline" onClick={onClose} data-testid="parent-link-map-close">Close</Button>
        </div>
      </div>
    </div>
  );
}
